import { ExportIcon } from 'components/icons';
import { ReactComponent as GoIcon } from 'components/icons/icon-go.svg';
import { ButtonCTA, CardBackground, Skeleton, Tooltip } from 'components/ui';
import { PAID_PLAN_TOOLTIP } from 'config.js';
import querystring from 'querystring';
import { Link } from 'react-router-dom';
import { useAppSelector } from 'store';
import { composableFiltersState } from 'store/composableFiltersSlice';
import {
  useExportCustomersListMutation,
  useGetDashboardLifecycleQuery,
} from 'store/services/segmentsApi';
import { SegmentInfo } from 'store/services/types';
import { userDetailsState } from 'store/userDetailsSlice';
import { useStoreSubscriptionPlan } from 'utils/useStoreSubscriptionPlan';

export default function AcquireNewCustomers() {
  const { currentStore } = useAppSelector(userDetailsState);

  const {
    data: lifecycleData,
    isFetching: lifecycleLoading,
  } = useGetDashboardLifecycleQuery(
    { serializedStoreName: currentStore.serializedStoreName },
    {
      skip: !!!currentStore.serializedStoreName,
    }
  );
  const lifecycle = lifecycleData?.lifecycle;

  return (
    <div className="flex flex-col">
      <h4 className="text-base tracking-wide md:text-2xl">
        Acquire new customers
      </h4>
      <p className="text-black text-segments-13px md:text-base text-opacity-30">
        Segments representing your top customers can be used to seed new
        Facebook custom audiences for lookalike campaigns. We provide each
        customer’s lifetime value to further refine your truly best of the best
        customers.
      </p>
      <div className="grid-cols-2 mt-2 md:grid md:mx-0 md:mt-6 md:gap-x-6">
        {lifecycleLoading ? (
          <SkeletonCardSegmentsTopCustomers />
        ) : (
          lifecycle?.acq_segments?.map((segment) => (
            <div className="mt-6 md:mt-0" key={segment.id}>
              <CardBackground>
                <div className="px-5 py-4 divide-y lg:px-10 lg:py-6">
                  <CardSegmentsTopCustomers segment={segment} />
                </div>
              </CardBackground>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function CardSegmentsTopCustomers({ segment }: { segment: SegmentInfo }) {
  const {
    storeSubscriptionPlan,
    subscriptionPlans,
  } = useStoreSubscriptionPlan();

  const { currentStore } = useAppSelector(userDetailsState);

  const currency = currentStore.completeInfo?.currency ?? 'usd';
  const locale = currentStore.completeInfo?.primary_locale ?? 'en';

  const [
    exportUsersToFile,
    { isLoading: fileDownloading },
  ] = useExportCustomersListMutation();

  const {
    data: { segments },
  } = useAppSelector(composableFiltersState);

  const completeSegmentInformation = segments?.find(
    (item) => item.id === segment.id
  );

  const handleExport = (content: string) => {
    const queryParams =
      currentStore.serializedStoreName &&
      querystring.stringify({
        store_name: currentStore?.completeInfo?.store_name,
        export_format: 'csv',
        export_content: content,
      });

    exportUsersToFile({
      serializedParams: queryParams,
      body: completeSegmentInformation?.json_filter,
      provider: content,
      ...(completeSegmentInformation?.segment_name && {
        segmentName: completeSegmentInformation.segment_name,
      }),
    });
  };

  return (
    <div className="py-4">
      <div>
        <p className="text-base font-bold truncate">{segment?.segment_name}</p>

        <p className="mt-1 leading-4 text-segments-13px md:text-base opacity-30">
          {segment?.description}
        </p>
      </div>
      <div className="mt-2">
        <p className="text-2xl leading-9 md:text-segments-32px">
          {segment?.segment_size?.toLocaleString(locale)}
        </p>

        <p className="text-base opacity-50">users</p>
      </div>
      <div className="mt-4">
        <p className="text-2xl leading-9 md:text-segments-32px">
          {segment?.aclv?.toLocaleString(locale, {
            style: 'currency',
            currency: currency,
          })}
        </p>

        <p className="text-base opacity-50">avg. CLV</p>
      </div>
      <Link
        to={{
          pathname: 'customers/create',
          search: querystring.stringify({
            store_name: currentStore.completeInfo?.store_name,
            segment_name: completeSegmentInformation?.segment_name,
          }),
        }}
        className="flex items-center mt-4 space-x-1"
      >
        <p className="font-bold leading-4 text-segments-13px text-segments-link">
          View segment{' '}
        </p>

        <GoIcon />
      </Link>

      <div className="w-64 mt-4 mb-2">
        <Tooltip
          text={PAID_PLAN_TOOLTIP}
          disabled={storeSubscriptionPlan > subscriptionPlans.small_business}
        >
          <ButtonCTA
            type="button"
            variant="secondary"
            disabled={
              fileDownloading ||
              storeSubscriptionPlan < subscriptionPlans.small_business
            }
            cursorNotAllowed={fileDownloading}
            onClick={() => handleExport('facebook')}
          >
            <div className="flex items-center justify-around px-3">
              {fileDownloading ? (
                <svg
                  className="w-5 h-5 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <>
                  <p>Get Facebook audience</p>
                  <ExportIcon className="w-5 h-5 text-black" />
                </>
              )}
            </div>
          </ButtonCTA>
        </Tooltip>
      </div>
    </div>
  );
}

function SkeletonCardSegmentsTopCustomers() {
  return (
    <div className="py-4 pt-6">
      <div>
        <Skeleton isLoaded={false}>
          <p className="h-4 " />
        </Skeleton>
        <Skeleton isLoaded={false}>
          <p className="h-4 mt-1 md:h-6" />
        </Skeleton>
      </div>
      <div className="mt-2">
        <Skeleton isLoaded={false}>
          <p className="h-8 md:h-9" />
          <p className="h-8" />
        </Skeleton>
      </div>
      <div className="mt-4">
        <Skeleton isLoaded={false}>
          <p className="h-8 md:h-9" />
          <p className="h-8" />
        </Skeleton>
      </div>
      <div className="mt-4">
        <Skeleton isLoaded={false}>
          <p className="h-8" />
        </Skeleton>
      </div>
      <Skeleton isLoaded={false}>
        <div className="w-56 mt-4 mb-2 h-segments-52px" />
      </Skeleton>
    </div>
  );
}
